<template>
  <form
    class="partner-search-ctrl js-partner-search-ctrl"
    novalidate
    @submit="handleSubmitEvent"
  >
    <div
      ref="partnerSearchCtrlRef"
      :class="getCtrlFieldCSSClasses"
    >
      <TextField
        id="employerSearchField"
        v-model="model"
        name="search"
        :autofocus="!supportsTouch()"
        :maxlength="200"
        :aria-label="$t('generic_employer_name')"
        data-qa="employer-search-field"
        :label="$t('generic_employer_name')"
        :disabled="ctrlItemDisabled"
        @focus="openDropdown"
        @input="(e) => handleTextInputChange(e)"
        @keyup="handleTextInputChange"
        @click.stop
      >
        <template #prepend>
          <SearchIcon />
        </template>
        <template #append>
          <KDSSpinner v-if="loading" />
        </template>
      </TextField>
      <div
        v-if="showSearchDropdownList"
        ref="partnerSearchDropdownRef"
        class="js-ctrl-dropdown partner-search-ctrl__dropdown-layout"
      >
        <div
          v-for="(partner, index) in searchResultsComputed"
          :id="'employer-search-option-' + partner.key"
          :key="partner.key"
          :aria-label="partner.title"
          :class="getDropdownItemCSSClasses(index)"
          :data-qa="'employer-search-option-' + partner.key"
          @click="handleSearchDropdownItemClick(partner)"
          v-html="partner.titleView"
        />
      </div>
    </div>
    <div class="partner-search-ctrl__button">
      <BaseButton
        :loading="interactiveButtonState === 'PROCESSING'"
        :success="interactiveButtonState === 'VALIDATED'"
        :text="$t('generic_check_coverage')"
        data-qa="employer-search-submit"
        @click="handleBaseButtonClick"
      />
    </div>
  </form>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
  watchEffect,
} from 'vue'
import normalSearchHighlight from '@shared/helpers/normalSearchHighlight'
import abbreviationSearchHighlight from '@shared/helpers/abbreviationSearchHighlight'
import { escapeHtml } from '@shared/utils/escapeHtml'
import BaseButton from '@shared/components/BaseButton.vue'
import Tracker from '@shared/Tracker'
import TextField from '@shared/components/form/TextField.vue'
import SearchIcon from '@shared/assets/icon/SearchIcon.vue'
import KDSSpinner from '@shared/components/KDSSpinner.vue'
import { supportsTouch } from '@shared/utils.js'

const ARROW_KEY_UP = 38
const ARROW_KEY_DOWN = 40
const ENTER = 13

const INVALIDATED_PARTNER = {
  id: -1,
  title: '',
  titleView: '',
  key: 'INVALIDATED_PARTNER',
  is_customer: false,
}

const getHighlightedTitle = (needle, title) =>
  normalSearchHighlight(needle, title) ||
  abbreviationSearchHighlight(needle, title) ||
  escapeHtml(title)

const getSearchedPartner = (suggestions, searchValue) => {
  const isSearchedPartner = (suggestion) => {
    if (suggestion.title.toLowerCase() === searchValue) {
      return true
    }
  }
  const partnerIndex = suggestions?.findIndex(isSearchedPartner)
  return suggestions?.[partnerIndex]
}

const props = defineProps({
  pendingResults: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})
const emit = defineEmits(['search', 'ctrlStateChange'])

const model = ref('')

const partnerSearchCtrlRef = ref(null)
const partnerSearchDropdownRef = ref(null)

const interactiveButtonState = ref('ACTIVE')
const nextValue = ref('')
const showSearchDropdownList = ref(false)
const selectedPartner = ref(null)
const highlightedPartnerIndex = ref(-1)
const suggestions = ref([])

const searchResultsComputed = computed(() => {
  if (!Array.isArray(suggestions.value)) {
    return []
  }
  return suggestions.value
    .filter(
      (suggestion) =>
        suggestion.key &&
        suggestion.title &&
        suggestion.key.length > 0 &&
        suggestion.title.length > 0,
    )
    .map((suggestion) =>
      Object.assign(suggestion, {
        titleView: getHighlightedTitle(nextValue.value, suggestion.title),
      }),
    )
})

const ctrlItemDisabled = computed(
  () =>
    interactiveButtonState.value === 'PROCESSING' ||
    interactiveButtonState.value === 'VALIDATED',
)

const getCtrlFieldCSSClasses = computed(() => [
  'partner-search-ctrl__field',
  ctrlItemDisabled.value ? 'partner-search-ctrl__field--disabled' : '',
])

watchEffect(() => (suggestions.value = props.pendingResults))

watch(suggestions, () => {
  openDropdown()
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleDocumentKeyDownEvent)
})

function handleTextInputChange(event) {
  if (
    event.keyCode === ENTER ||
    event.keyCode === ARROW_KEY_DOWN ||
    event.keyCode === ARROW_KEY_UP
  ) {
    return
  }
  if (!(event instanceof KeyboardEvent || event instanceof InputEvent)) {
    return
  }
  suggestions.value = []
  const input = event.target.value.trim().toLowerCase()
  emit('ctrlStateChange', { state: 'active' })
  if (nextValue.value !== input) {
    emit('search', input)
  }
  nextValue.value = input
  interactiveButtonState.value = 'ACTIVE'
}

function handleSearchDropdownItemClick(partner) {
  showSearchDropdownList.value = false
  selectedPartner.value = partner
  model.value = partner.title
  handleBaseButtonClick()
}

function handleBaseButtonClick() {
  interactiveButtonState.value = 'PROCESSING'
  emit('ctrlStateChange', { state: 'processing' })
  const currentSearchValue = model.value.toLowerCase()
  const searchedPartner = getSearchedPartner(
    suggestions.value,
    currentSearchValue,
  )
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.select_employer_search',
      action: 'select',
      object_type: 'employer_search',
      screen_name: 'onb_employer_search',
    },
    {
      search_value: currentSearchValue,
      search_success: !!searchedPartner,
    },
  )
  if (searchedPartner) {
    selectedPartner.value = searchedPartner
    validateSelectedPartner(selectedPartner.value)
  } else {
    interactiveButtonState.value = 'ACTIVE'
    emit('ctrlStateChange', {
      state: 'disabled',
      value: currentSearchValue,
    })
  }
}

function validateSelectedPartner(partner) {
  const selected = partner == null ? INVALIDATED_PARTNER : partner
  setTimeout(() => {
    if (selected.is_customer) {
      interactiveButtonState.value = 'VALIDATED'
      setTimeout(() => {
        emit('ctrlStateChange', {
          state: 'validated',
          value: selected,
        })
      }, 1000)
    } else {
      interactiveButtonState.value = 'ACTIVE'
      emit('ctrlStateChange', {
        state: 'disabled',
        value: selected,
      })
    }
  }, 500)
}

function handleDocumentKeyDownEvent(event) {
  if (event.keyCode === ARROW_KEY_DOWN) {
    const nextIndex = highlightedPartnerIndex.value + 1
    highlightedPartnerIndex.value =
      nextIndex <= searchResultsComputed.value.length - 1 ? nextIndex : 0
    partnerSearchDropdownRef.value?.children[
      highlightedPartnerIndex.value
    ]?.scrollIntoView({ block: 'nearest' })
  } else if (event.keyCode === ARROW_KEY_UP) {
    const nextIndex = highlightedPartnerIndex.value - 1
    highlightedPartnerIndex.value =
      nextIndex < 0 ? searchResultsComputed.value.length - 1 : nextIndex
    partnerSearchDropdownRef.value?.children[
      highlightedPartnerIndex.value
    ]?.scrollIntoView({ block: 'nearest' })
  } else if (event.keyCode === ENTER && !props.loading) {
    event.preventDefault()
    const nextPartner =
      searchResultsComputed.value[highlightedPartnerIndex.value]
    if (nextPartner != null) {
      handleSearchDropdownItemClick(nextPartner)
    }
  }
}

function openDropdown() {
  showSearchDropdownList.value = searchResultsComputed.value.length > 0
  if (showSearchDropdownList.value) {
    nextTick(() =>
      partnerSearchCtrlRef.value?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      }),
    )
    highlightedPartnerIndex.value = 0
    document.addEventListener('keydown', handleDocumentKeyDownEvent)
  } else {
    highlightedPartnerIndex.value = -1
    selectedPartner.value = null
    document.removeEventListener('keydown', handleDocumentKeyDownEvent)
  }
}

function getDropdownItemCSSClasses(partnerIndex) {
  return [
    'partner-search-ctrl__dropdown-layout__item',
    'js-ctrl-dropdown__item',
    highlightedPartnerIndex.value === partnerIndex
      ? 'partner-search-ctrl__dropdown-layout__item--selected js-ctrl-dropdown__item-selected'
      : '',
  ]
}

function handleSubmitEvent(event) {
  if (
    !!suggestions.value[0] &&
    suggestions.value[0].title.toLowerCase() === nextValue.value.toLowerCase()
  ) {
    selectedPartner.value = suggestions.value[0]
  }
  handleBaseButtonClick()
  event.preventDefault()
}
</script>

<style lang="scss">
.partner-search-ctrl {
  &__dropdown-layout {
    width: 100%;
    max-height: 150px;
    overflow-y: auto;
    border-radius: 4px;
    border: 3px solid $kds-color-line-on-interactive;
    background-color: $kds-color-background-base;
    box-sizing: border-box;
    padding: 4px 0;

    &__item {
      padding: 10px 0;
      padding-left: 15px;
      cursor: pointer;

      &:hover,
      &--selected {
        background-color: $kds-color-background-interactive-subtle-hover;
      }
    }
  }

  &__button {
    display: inline-block;
    width: 100%;
    margin-top: $kds-space-2_5x;
    text-align: center;

    .base-button {
      width: 100%;
    }
  }

  &__dropdown-layout {
    margin-top: 4px;
  }
}
</style>
